import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Subject } from 'rxjs';
import { MenuController } from '@ionic/angular';
import type { AccionVisita } from '../components/formulario-visita/formulario-visita.component';
import type { Visita } from './visitas.service';
import { NetworkService } from './network.service';
import { VisitaOfflinePanelService } from './visita-offline-panel.service';

/** Id del ion-menu usado como panel de visitas (segundo menú, side="end"). */
export const VISITA_PANEL_MENU_ID = 'visita-panel';

/**
 * Servicio global del panel lateral de visitas (Agendar, Ver Detalles, Reagendar, Registrar).
 * Usa un segundo ion-menu (side="end") para aprovechar las animaciones nativas de Ionic.
 * @see https://ionicframework.com/docs/api/menu#multiple-menus
 */
@Injectable({ providedIn: 'root' })
export class VisitaPanelService {
  private readonly menuController = inject(MenuController);
  private readonly network = inject(NetworkService);
  private readonly offlinePanel = inject(VisitaOfflinePanelService);
  private readonly router = inject(Router);
  private readonly _accion = new BehaviorSubject<AccionVisita | null>(null);
  private readonly _visita = new BehaviorSubject<Visita | null>(null);
  private readonly _permitirPdf = new BehaviorSubject<boolean>(false);
  private readonly _guardado = new Subject<void>();

  /** Emisión cuando se guarda una visita (para que la lista recargue). */
  readonly guardado$ = this._guardado.asObservable();

  getAccion(): AccionVisita | null {
    return this._accion.value;
  }

  getVisita(): Visita | null {
    return this._visita.value;
  }

  getPermitirPdf(): boolean {
    return this._permitirPdf.value;
  }

  /** Id del ion-menu (para usar en template y MenuController). */
  get menuId(): string {
    return VISITA_PANEL_MENU_ID;
  }

  /** Abre el panel (ion-menu) con la acción y opcionalmente la visita. */
  abrir(accion: AccionVisita, visita?: Visita | null, permitirPdf: boolean = false): void {
    if (accion === 'nueva' && !this.network.isOnline()) {
      const ruta = this.router.url.split('?')[0];
      const irARutaDia = ruta !== '/ruta-del-dia' && !ruta.startsWith('/ruta-del-dia/');
      const abrirOffline = (): void => {
        this.offlinePanel.abrir();
      };
      if (irARutaDia) {
        void this.router.navigate(['/ruta-del-dia'], { replaceUrl: true }).then(() => abrirOffline());
      } else {
        abrirOffline();
      }
      return;
    }

    this._accion.next(accion);
    this._visita.next(visita ?? null);
    this._permitirPdf.next(permitirPdf);
    // Abrir en el siguiente tick para que Angular actualice [disabled] y el contenido del menú; evita tener que pulsar dos veces
    setTimeout(() => {
      this.menuController.open(VISITA_PANEL_MENU_ID);
    }, 0);
  }

  /** Cierra el panel (ion-menu); las animaciones las gestiona Ionic. */
  cerrar(): void {
    this.menuController.close(VISITA_PANEL_MENU_ID);
  }

  /** Llamado desde (ionDidClose) del menú para limpiar el estado. */
  alCerrarMenu(): void {
    this._accion.next(null);
    this._visita.next(null);
    this._permitirPdf.next(false);
  }

  /** Llamado cuando el formulario emite visibleChange(false) (ej. botón Cerrar). */
  onVisibleChange(visible: boolean): void {
    if (!visible) {
      this.cerrar();
    }
  }

  /** Llamado cuando el formulario emite guardado. */
  onGuardado(): void {
    this._guardado.next();
  }
}
